"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Star, Quote, Hand } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const testimonials = [{
  name: "Sophie Miller",
  position: "Founder, Maison Boutique",
  quote: "Sunday Velvet has redefined the guest experience in our suites. The scent lingers like a soft memory, and the texture is pure silk.",
  metric: "5-Star Rating",
  topVoice: true,
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1637664/img/sophie_miller.png"
}, {
  name: "Emma Thompson",
  position: "Lifestyle Curator",
  quote: "Finally, a soap that doesn't irritate my sensitive skin. It's become the most beautiful and intentional part of my morning ritual.",
  metric: "Skin-Safe",
  topVoice: false,
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1637664/img/emma_thompson.png"
}, {
  name: "Julian Vance",
  position: "Airbnb Superhost",
  quote: "Our guests constantly ask where they can buy these soaps. They add an immediate touch of luxury to even the smallest spaces.",
  metric: "Guest Favorite",
  topVoice: true,
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1637664/img/julian_vance.png"
}, {
  name: "Clara Rossi",
  position: "Wellness Editor",
  quote: "Editorial-grade aesthetics meet high-performance skincare. Sunday Velvet is the only brand I trust for my family's daily care.",
  metric: "Daily Ritual",
  topVoice: false,
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1637664/img/clara_rossi.png"
}];
export default function StudentBreakthroughs() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="222774" id="bathside-stories" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-accent/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-secondary/30 rounded-full blur-[150px] pointer-events-none" />
      
      {}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03] select-none">
        <span className="text-[40rem] font-serif leading-none">V</span>
      </div>

      <div ref={ref} className={`container mx-auto px-4 relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-default font-bold mb-6 tracking-tight">
            Bathside <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent-foreground to-primary">Stories</span>
          </h2>
          <div className="flex items-center justify-center gap-3 text-muted-foreground text-xs md:text-sm font-mono uppercase tracking-[0.2em]">
            <Hand className="w-4 h-4 animate-bounce" />
            <span>A Sensory Journey</span>
          </div>
        </div>

        <Carousel opts={{
        align: "start",
        loop: true
      }} className="w-full cursor-grab active:cursor-grabbing">
          <CarouselContent className="-ml-4 md:-ml-6 pb-12 pt-16">
            {testimonials.map((testimonial, i) => <CarouselItem key={i} data-index={i} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3">
                {}
                <Card className="h-full relative bg-card/30 backdrop-blur-md border-border/40 shadow-sm flex flex-col justify-between group hover:border-accent/40 transition-colors duration-700 rounded-3xl overflow-visible pt-14">
                  
                  {}
                  <div className="absolute -top-12 left-1/2 -translate-x-1/2 z-20">
                    <div className={`relative rounded-full p-1 transition-transform duration-700 group-hover:scale-105 ${testimonial.topVoice ? "bg-gradient-to-tr from-accent via-background to-accent-foreground shadow-sm" : "bg-border/30"}`}>
                      <SafeHydrate><Avatar className="w-24 h-24 border-4 border-background bg-background">
                        <AvatarImage src={testimonial.avatar} alt={testimonial.name} className="object-cover" />
                        <AvatarFallback className="text-2xl font-default font-medium bg-secondary text-muted-foreground">
                          {testimonial.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                      
                      {}
                      {testimonial.topVoice && <div className="absolute -bottom-2 -right-2 bg-background rounded-full p-1.5 shadow-sm border border-border">
                          <Star className="w-5 h-5 text-accent-foreground fill-accent-foreground" />
                        </div>}
                    </div>
                  </div>

                  <CardContent className="flex-grow flex flex-col pt-6 px-8 text-center items-center">
                    <Quote className="w-8 h-8 text-accent/40 mb-8 rotate-180" />
                    <p className="text-xl md:text-2xl text-foreground/80 font-serif italic leading-relaxed mb-8 flex-grow">
                      "{testimonial.quote}"
                    </p>
                    <div className="mt-auto">
                      <h4 className="font-bold text-lg font-default text-foreground tracking-wide uppercase">
                        {testimonial.name}
                      </h4>
                      <p className="text-xs text-muted-foreground font-mono mt-1 tracking-wider">
                        {testimonial.position}
                      </p>
                    </div>
                  </CardContent>

                  <CardFooter className="px-8 pb-8 pt-6 border-t border-border/20 mt-6 text-center">
                    <div className="w-full">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-[0.25em] mb-3 font-semibold">
                        Impact
                      </p>
                      {}
                      <div className="text-3xl md:text-4xl font-default font-bold text-primary tracking-tight opacity-90">
                        {testimonial.metric}
                      </div>
                    </div>
                  </CardFooter>
                </Card>
              </CarouselItem>)}
          </CarouselContent>
          
          {}
          <div className="flex items-center justify-center gap-6 mt-8">
            <CarouselPrevious className="static translate-y-0 translate-x-0 w-10 h-10 rounded-full bg-transparent border-border/60 text-foreground/60 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all" />
            <CarouselNext className="static translate-y-0 translate-x-0 w-10 h-10 rounded-full bg-transparent border-border/60 text-foreground/60 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all" />
          </div>
        </Carousel>
      </div>
    </section>;
}